import React from "react";
import { Box, Stack, Typography, Card, CircularProgress } from "@mui/material";
import "../../../assets/css/SalesTrend/SaleData.css";
import ArrowUpwardIcon from "../../../assets/images/ArrowUpwardIcon_green.svg";
import ArrowDownwardIcon from "../../../assets/images/ArrowDownwardIcon_red.svg";
import { useSalesContext } from "../../SalesContext";

const SaleData = () => {
  const { kpiData, salesUnit, loading } = useSalesContext();
  const defaultCardData = {
    card1_year_sale: {
      LYTD: "0",
      Target: "0",
      CYTD: "0",
      Percentage_Archive: "0",
      Growth: "0",
    },
    card2_quarter_sale: {
      LYQTD: "0",
      Target: "0",
      CQTD: "0",
      Percentage_Archive: "0",
      Growth: "0",
    },
    card3_month_sale: {
      LYMTD: "0",
      Target: "0",
      CMTD: "0",
      Percentage_Archive: "0",
      Growth: "0",
    },
  };

  const cardConfig = [
    {
      title: `Year Sale (${salesUnit || "Lacs"})`,
      color: "#F2F6E5",
      data:
        kpiData?.kpi_data?.cards_data?.card1_year_sale ||
        defaultCardData.card1_year_sale,
      keys: ["LYTD", "Target", "CYTD", "Percentage_Archive", "Growth"],
      displayKeys: ["LYTD", "Target", "CYTD", "%Ach", "Grw"],
    },
    {
      title: `Quarter Sale (${salesUnit || "Lacs"})`,
      color: "#E5ECF6",
      data:
        kpiData?.kpi_data?.cards_data?.card2_quarter_sale ||
        defaultCardData.card2_quarter_sale,
      keys: ["LYQTD", "Target", "CQTD", "Percentage_Archive", "Growth"],
      displayKeys: ["LYQTD", "Target", "CQTD", "%Ach", "Grw"],
    },
    {
      title: `Month Sale (${salesUnit || "Lacs"})`,
      color: "#E6F6FF",
      data:
        kpiData?.kpi_data?.cards_data?.card3_month_sale ||
        defaultCardData.card3_month_sale,
      keys: ["LYMTD", "Target", "CMTD", "Percentage_Archive", "Growth"],
      displayKeys: ["LYMTD", "Target", "CMTD", "%Ach", "Grw"],
    },
  ];

  const formatValue = (value, displayKey, salesUnit = "") => {
    const numValue = Number(value);
    const isNegative = numValue < 0;

    if (!isFinite(numValue) && !isNaN(numValue)) {
      return "0";
    }

    if (isNaN(numValue)) {
      return 0;
    }

    let adjustedValue = numValue;
    if (displayKey !== "%Ach" && displayKey !== "Grw") {
      if (salesUnit === "Lacs") {
        adjustedValue = numValue / 100000;
      } else if (salesUnit === "Crores") {
        adjustedValue = numValue / 10000000;
      }
    }

    const formattedNum =
      (Math.floor(Math.abs(adjustedValue) * 10) / 10).toFixed(1) *
      (isNegative ? -1 : 1);

    if (displayKey === "%Ach" || displayKey === "Grw") {
      return `${formattedNum}%`;
    }
    return formattedNum;
  };

  const getYesterdayDate = () => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    const options = { day: "2-digit", month: "short", year: "2-digit" };
    return yesterday.toLocaleDateString("en-GB", options).replace(/ /g, "-");
  };

  if (loading || !kpiData) {
    return (
      <Box
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box className="header">
        <Box className="title-container">
          <Typography className="Filter-title" sx={{ fontSize: "14px" }}>
            Data Till Date {getYesterdayDate()}
          </Typography>
        </Box>
      </Box>

      <Stack
        className="stack-container"
        direction={{ xs: "column", sm: "row" }}
      >
        {cardConfig.map((sale, index) => (
          <Card
            key={index}
            className="stack-item"
            sx={{
              backgroundColor: sale.color,
              borderRadius: "16px",
              boxShadow: "none",
            }}
          >
            <Box className="stacktitle-container">
              <Typography className="title-text-2" fontWeight="bold">
                {sale.title}
              </Typography>
            </Box>

            <Stack
              className="stack-content"
              direction="row"
              sx={{ gap: "2px" }}
            >
              {sale.displayKeys.map((item, idx) => {
                const value = sale.data[sale.keys[idx]];
                const numValue = Number(value);
                const isNegative = numValue < 0;
                const isPositive = numValue > 0;
                const isZeroOrInvalid =
                  numValue === 0 || isNaN(numValue) || !isFinite(numValue);
                const formattedValue = formatValue(value, item, salesUnit);

                return (
                  <Box
                    key={idx}
                    sx={{
                      flex: 1,
                      height: "65px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      sx={{
                        fontSize: "0.781rem",
                        textAlign: "center",
                      }}
                    >
                      {item}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {item === "Grw" &&
                        formattedValue !== "-" &&
                        !isZeroOrInvalid && (
                          <img
                            src={
                              isNegative ? ArrowDownwardIcon : ArrowUpwardIcon
                            }
                            alt={isNegative ? "down arrow" : "up arrow"}
                            style={{ width: "12px", height: "12px" }}
                          />
                        )}
                      <Typography
                        sx={{
                          fontSize: "13px",
                          color:
                            item === "%Ach"
                              ? isZeroOrInvalid
                                ? "black"
                                : numValue < 100
                                ? "red"
                                : "green"
                              : item === "Grw"
                              ? isZeroOrInvalid
                                ? "black"
                                : isNegative
                                ? "red"
                                : isPositive
                                ? "green"
                                : "black"
                              : "inherit",
                          textAlign: "center",
                        }}
                        variant="body2"
                      >
                        {formattedValue}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </Card>
        ))}
      </Stack>
    </Box>
  );
};

export default SaleData;
